import React, { useEffect } from "react";
import { formatEntryThumbnailUrl, HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import {
    Modal,
    modalClasses,
    ModalCloseButton,
    ModalContent,
    modalContentClasses,
    Typography,
} from "@kaltura/ds-react-components";
import { EventPlatformEntry } from "@kaltura/mediaspace-shared-types";
import { encodeUnicode, translate } from "@kaltura/mediaspace-shared-utils";
import { EventPresentersListNoPopover } from "../event-presenter/event-presenters-list-no-popover/EventPresentersListNoPopover";
import styled from "@emotion/styled";
import ActionSection from "../event-list-item/action-section/ActionSection";
import { EventListItemScheduling } from "../event-list-item/EventListItemScheduling";

export interface EventListItemModalProps {
    show: boolean;
    onHide: () => void;
    data: EventPlatformEntry;
    modalBoxMaskColor?: string;
    inWatchlist?: boolean;
    callToActionButton: React.ReactNode;
    onToggleWatchList?: (id: string, add: boolean) => void;
}

const modalPaperClass = "kms-ds-agenda-modal-paper";

// region Styled components
const StyledModal = styled(Modal)({
    [`& .${modalClasses.paper}.${modalPaperClass}`]: {
        border: "none",
        maxWidth: "583px",
    },
});

const ModalBody = styled(ModalContent)({
    maxHeight: "none",
    wordBreak: "break-word",

    [`.${modalContentClasses.root}&`]: {
        padding: 0,
    },
});

const ModalCloseBtn = styled(ModalCloseButton)({
    zIndex: 1,
});

const ThumbnailWrapper = styled("div")(({ theme }) => ({
    width: "100%",
    height: 328,
    display: "flex",
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        height: "auto",
    },
}));

const Thumbnail = styled("img")(({ theme }) => ({
    height: "auto",
    objectFit: "cover",
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        borderRadius: 0,
        borderBottom: "none",
    },
    marginRight: "-1px",
    width: "calc(100% + 1px)",
}));

const Info = styled("div")(({ theme }) => ({
    padding: theme.spacing(4),
    [theme.breakpoints.down(theme.breakpoints.values.xs)]: {
        padding: theme.spacing(2),
    },
}));

const Name = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone1,
    margin: theme.spacing(0, 0, 2),
}));

const Description = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    color: theme.kaltura.palette.tone1,
    [theme.breakpoints.down(theme.breakpoints.values.xs)]: {
        marginTop: theme.spacing(2),
    },
}));

const Action = styled(ActionSection)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));
// endregion

/**
 * Event List Modal
 */
export const EventListItemModal: React.FC<EventListItemModalProps> = (props: EventListItemModalProps) => {
    const { show, onHide, data, inWatchlist, callToActionButton, onToggleWatchList } = props;

    const { name, description = "", presenters = [] } = data;

    useEffect(() => {
        if (show) {
            document.body.classList.add("event-list-item-modal-open");
        }
        else {
            document.body.classList.remove("event-list-item-modal-open");
        }

        return () => {
            document.body.classList.remove("event-list-item-modal-open");
        };
    }, [show]);

    return (
        <StyledModal
            variant={"content"}
            className="event-list-item-modal"
            open={show}
            onClose={onHide}
            classes={{ paper: modalPaperClass }}
            data-analytics={encodeUnicode(name)}
            aria-labelledby={"kms-ds-event-modal"}
        >
            <ModalCloseBtn variant={"pill"} onClick={onHide} aria-label={translate("close")} />
            <ModalBody>
                <div>
                    <ThumbnailWrapper className="kms-ds-event-list-item-modal-thumbnail">
                        <Thumbnail
                            src={formatEntryThumbnailUrl(data.thumbnailUrl, "auto", "auto")}
                            alt={translate("%1 session image", [data.name])}
                        />
                    </ThumbnailWrapper>
                    <Info className={"kms-ds-event-list-item-modal-content"}>
                        {data.schedulingData && (
                            <EventListItemScheduling schedulingData={data.schedulingData} showDate={false} />
                        )}
                        <Name variant={"h4"} id={"kms-ds-event-modal"}>
                            {name}
                        </Name>
                        <Action
                            data={data}
                            callToActionButton={callToActionButton}
                            inWatchlist={inWatchlist}
                            className={"kms-ds-event-list-item-modal-content-actions"}
                            onToggleWatchList={onToggleWatchList}
                        />
                        <EventPresentersListNoPopover presenters={presenters} />
                        <Description className="kms-ds-event-list-item-modal-content-description">
                            {HtmlReactParser(description)}
                        </Description>
                    </Info>
                </div>
            </ModalBody>
        </StyledModal>
    );
};

export default EventListItemModal;
